import React from 'react';
import PropTypes from 'prop-types';

import useCountdown, { Duration } from '../../../hooks/useCountdown';
import { IClassNames, makeClassNames } from '../../../utils/styles';
import { MatchStatusType, MatchStatusList } from '../../../typings/match';

import './match-countdown.scss';
import { useAnimateCountdown, useShouldRenderMatchCountdown } from './match-countdown-hooks';

export interface IMatchCountdown {
  tick?: number;
  matchDate?: string;
  matchStatus: MatchStatusType;
  enabled?: boolean;
}

const formatSecAndMin = (num: number) => String(num >= 0 ? num : 0).padStart(2, '0');

const classNames = (animateCountdown: boolean, isPreMatch: boolean): IClassNames =>
  makeClassNames({
    result: ['match-countdown__result'],
    countdown: ['match-countdown', isPreMatch && animateCountdown && 'match-countdown--animated'],
    countdownTime: ['match-countdown__time'],
  });

const MatchCountdown: React.FC<React.PropsWithChildren<IMatchCountdown>> = ({
  matchDate,
  tick = 1000,
  children,
  matchStatus,
  enabled = true,
}) => {
  const [isBeforeMatch, isBeforeMatchTimeout] = useShouldRenderMatchCountdown({
    status: matchStatus,
    startDate: matchDate,
    enabled,
  });

  const { minutes, seconds, duration }: Duration = useCountdown(Date.parse(matchDate ?? ''), tick, isBeforeMatch);
  const [animateCountdown, isMatchStarted] = useAnimateCountdown(duration, isBeforeMatch);
  const cn = classNames(animateCountdown, matchStatus === 'PreMatch');

  const formattedMinutes = formatSecAndMin(minutes);
  const formattedSeconds = formatSecAndMin(seconds);

  return (
    <span className={cn.countdown}>
      {isBeforeMatchTimeout && !isMatchStarted ? (
        <span className={cn.countdownTime}>
          {`${formattedMinutes}:${formattedSeconds}`.split('').map((letter, index) => (
            <span key={index}>{letter}</span>
          ))}
        </span>
      ) : (
        <span className={cn.result}>{children}</span>
      )}
    </span>
  );
};

/* istanbul ignore else */ if (process.env.NODE_ENV !== 'production') {
  MatchCountdown.propTypes = {
    enabled: PropTypes.bool,
    matchDate: PropTypes.string,
    matchStatus: PropTypes.oneOf(MatchStatusList).isRequired,
    tick: PropTypes.number,
  };
}

export default MatchCountdown;
